/* import __COLOCATED_TEMPLATE__ from './escalation.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import type Settings from 'embercom/models/ai-agent/tone-of-voice-settings';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type EscalationRule from 'embercom/models/ai-agent/escalation-rule';
import {
  DEFAULT_MAX_GUIDELINE_COUNT,
  INCREASED_MAX_GUIDELINE_COUNT,
} from 'embercom/components/ai-agent/guidance';
import { hasFeature } from 'embercom/helpers/has-feature';
import type Router from '@ember/routing/router-service';
import { EntityType } from 'embercom/models/data/entity-types';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

const SURVEY_ID = 44017774;
enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

export type EscalationCategoryType = 'rule-based' | 'scenario-based';

interface CategoryOption {
  id: EscalationCategoryType;
  title: string;
  description: string;
  icon: InterfaceIconName;
}

interface Args {
  openSectionId?: string;
}
export default class AiAgentEscalation extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare router: Router;

  readonly CHAT_TAB = 'chat';
  readonly VOICE_TAB = 'voice';
  @tracked activeTab = this.CHAT_TAB;
  @tracked openSectionId = '';
  @tracked personalityCustomizationSettings?: Settings;
  @tracked previewLocale = 'en';
  @tracked showPreview = true;
  @tracked selectedAiContentSegmentIds: number[] = [];
  @tracked searchTerm: string | undefined;
  @tracked selectedCategories: EscalationCategoryType[] = [];
  @tracked selectedChannels: string[] = [];

  get useReactPreview(): boolean {
    return hasFeature('react-preview-panel', this.appService);
  }

  get hasChannelSelectionFF(): boolean {
    return hasFeature('fin-guidance-channel-selection', this.appService);
  }

  get MAX_GUIDELINE_COUNT(): number {
    if (hasFeature('increased-guidance-limit', this.appService)) {
      return INCREASED_MAX_GUIDELINE_COUNT;
    }
    if (hasFeature('fin-guidance-dynamic-limit', this.appService)) {
      return Infinity;
    }
    return DEFAULT_MAX_GUIDELINE_COUNT;
  }

  get showVoiceEscalation() {
    if (this.hasChannelSelectionFF) {
      return false;
    }
    return !this.appService.app.isStandaloneApp || this.appService.app.canUseStandaloneDeployPhone;
  }

  Entity_Type = EntityType.EscalationRule;

  get categories(): CategoryOption[] {
    if (this.hasChannelSelectionFF) {
      // When channel selection is on, show all categories unified (no tabs)
      return [
        {
          id: 'rule-based',
          title: this.intl.t('ai-agent.guidance.guidelines.categories.rule-based-routing.title'),
          description: this.intl.t(
            'ai-agent.guidance.guidelines.categories.rule-based-routing.description-with-link',
            { url: this.router.urlFor(this.categoriesRoute, this.appService.app.id) },
          ),
          icon: 'insert-data',
        },
        {
          id: 'scenario-based',
          title: this.intl.t(
            'ai-agent.guidance.guidelines.categories.scenario-based-routing.title',
          ),
          description: this.intl.t(
            'ai-agent.guidance.guidelines.categories.scenario-based-routing.description',
          ),
          icon: 'guidance',
        },
      ];
    }

    if (this.activeTab === this.VOICE_TAB) {
      // Voice only has scenario-based routing (no rule-based)
      return [
        {
          id: 'scenario-based',
          title: this.intl.t('ai-agent.guidance.guidelines.categories.voice-routing.title'),
          description: this.intl.t(
            'ai-agent.guidance.guidelines.categories.voice-routing.description',
          ),
          icon: 'guidance',
        },
      ];
    }

    // Chat tab categories (existing)
    return [
      {
        id: 'rule-based',
        title: this.intl.t('ai-agent.guidance.guidelines.categories.rule-based-routing.title'),
        description: this.intl.t(
          'ai-agent.guidance.guidelines.categories.rule-based-routing.description-with-link',
          { url: this.router.urlFor(this.categoriesRoute, this.appService.app.id) },
        ),
        icon: 'insert-data',
      },
      {
        id: 'scenario-based',
        title: this.intl.t('ai-agent.guidance.guidelines.categories.scenario-based-routing.title'),
        description: this.intl.t(
          'ai-agent.guidance.guidelines.categories.scenario-based-routing.description',
        ),
        icon: 'guidance',
      },
    ];
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.getPersonalityCustomizationSettings();

    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }

    if (args.openSectionId) {
      this.openSectionId = args.openSectionId;
    }

    // Load correct tab from URL
    this.loadCorrectTab();
  }

  private loadCorrectTab() {
    let currentTabParam = this.showVoiceEscalation
      ? this.router.currentRoute?.queryParams?.escalation_tab
      : this.CHAT_TAB;

    if (!currentTabParam) {
      return;
    }

    if (currentTabParam === this.CHAT_TAB || currentTabParam === this.VOICE_TAB) {
      this.activeTab = currentTabParam;
    }
  }

  get routingGuidanceChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.escalationGuidelines.forEach((guideline) => {
      hash[guideline.id] = guideline.chartDefinitions;
    });
    return hash;
  }

  get escalationRuleChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.escalationRules.forEach((escalationRule) => {
      hash[escalationRule.id] = escalationRule.chartDefinitions;
    });
    return hash;
  }

  async getPersonalityCustomizationSettings(): Promise<$TSFixMe> {
    this.personalityCustomizationSettings = await this.store.findRecord(
      'ai-agent/tone-of-voice-settings',
      this.appService.app.id,
    );
  }

  get escalationRules(): EscalationRule[] {
    return this.store.peekAll('ai-agent/escalation-rule').toArray();
  }

  get filteredEscalationRules(): EscalationRule[] {
    // Apply category filter
    if (this.selectedCategories.length > 0 && !this.selectedCategories.includes('rule-based')) {
      return [];
    }

    let filtered = this.escalationRules;

    // Apply search term filter
    if (this.searchTerm && this.searchTerm.length > 0) {
      filtered = filtered.filter((rule) => {
        let name = rule.name?.toLowerCase() || '';
        let searchTerm = this.searchTerm!.toLowerCase();

        return name.includes(searchTerm);
      });
    }

    // Apply audience filter
    if (this.selectedAiContentSegmentIds.length > 0) {
      filtered = filtered.filter((rule) =>
        this.selectedAiContentSegmentIds.some((id) => rule.aiContentSegmentIds.includes(id)),
      );
    }

    filtered = this.filterBySelectedChannels(filtered);

    return filtered;
  }

  get filteredEscalationGuidelines(): Guideline[] {
    // Apply category filter
    if (this.selectedCategories.length > 0 && !this.selectedCategories.includes('scenario-based')) {
      return [];
    }

    let filtered = this.escalationGuidelines;

    // Apply search term filter
    if (this.searchTerm && this.searchTerm.length > 0) {
      filtered = filtered.filter((guideline) => {
        let title = guideline.title?.toLowerCase() || '';
        let prompt = guideline.prompt?.toLowerCase() || '';
        let searchTerm = this.searchTerm!.toLowerCase();

        return title.includes(searchTerm) || prompt.includes(searchTerm);
      });
    }

    // Apply audience filter
    if (this.selectedAiContentSegmentIds.length > 0) {
      filtered = filtered.filter((guideline) =>
        this.selectedAiContentSegmentIds.some((id) => guideline.aiContentSegmentIds.includes(id)),
      );
    }

    filtered = this.filterBySelectedChannels(filtered);

    return filtered;
  }

  private filterBySelectedChannels<T extends { targetChannels: string[] }>(items: T[]): T[] {
    if (this.selectedChannels.length === 0) {
      return items;
    }
    return items.filter((item) => {
      let channels = item.targetChannels;
      if (!channels || channels.length === 0) {
        return true;
      }
      return this.selectedChannels.some((ch) => channels.includes(ch));
    });
  }

  get hasSearchOrFilter(): boolean {
    return (
      (this.searchTerm && this.searchTerm.length > 0) ||
      this.selectedAiContentSegmentIds.length > 0 ||
      this.selectedCategories.length > 0 ||
      this.selectedChannels.length > 0
    );
  }

  get totalFilteredResults(): number {
    return this.filteredEscalationRules.length + this.filteredEscalationGuidelines.length;
  }

  get guidelines(): Guideline[] {
    return this.store.peekAll('ai-agent/guidelines').rejectBy('isDeleted');
  }

  get escalationGuidelines(): Guideline[] {
    if (this.hasChannelSelectionFF) {
      return this.guidelines
        .filter((g) => g.category === 'routing' || g.category === 'voice_routing')
        .toArray();
    }
    return this.guidelines.filterBy('category', this.escalationCategory).toArray();
  }

  get escalationCategory(): GuidelineCategory {
    return this.activeTab === this.VOICE_TAB ? 'voice_routing' : 'routing';
  }

  get escalationGuidelineTitle(): string {
    return this.activeTab === this.VOICE_TAB
      ? this.intl.t('ai-agent.guidance.guidelines.categories.voice-routing.title')
      : this.intl.t('ai-agent.guidance.guidelines.categories.scenario-based-routing.title');
  }

  get escalationGuidelineDescription(): string {
    return this.activeTab === this.VOICE_TAB
      ? this.intl.t('ai-agent.guidance.guidelines.categories.voice-routing.description')
      : this.intl.t('ai-agent.guidance.guidelines.categories.scenario-based-routing.description');
  }

  get hasInvalidGuidelines(): boolean {
    return this.escalationGuidelines.some((guideline) => guideline.validations.isInvalid);
  }

  get validationErrors(): string[] {
    return this.escalationGuidelines
      .filter((guideline) => guideline.validations.isInvalid)
      .map((guideline) => guideline.validations.messages)
      .flat()
      .uniq();
  }

  get hasUnsavedChanges(): boolean {
    return this.escalationGuidelines.some(
      (guideline) => guideline.hasDirtyAttributes || guideline.isDeleted,
    );
  }

  get disableSaveButton(): boolean {
    return this.hasInvalidGuidelines || !this.hasUnsavedChanges;
  }

  get hasContentReadyForFin() {
    return this.aiAgentSetupService.hasContentReadyForFin;
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  get categoriesRoute() {
    if (this.appService.app.isStandaloneApp) {
      return 'apps.app.standalone.attributes';
    }
    return 'apps.app.automation.fin-ai-agent.attributes';
  }

  get escalationRulesDescription() {
    let categoriesUrl = this.router.urlFor(this.categoriesRoute, this.appService.app.id);
    return this.intl.t(
      'ai-agent.guidance.guidelines.categories.rule-based-routing.description-with-link',
      {
        htmlSafe: true,
        url: categoriesUrl,
      },
    );
  }

  async _saveChangedGuidelines(): Promise<void> {
    // .save() by itself is sending requests for all guidelines
    let unsavedGuidelines = this.escalationGuidelines.filter(
      (guideline) => guideline.hasDirtyAttributes,
    );

    for (let guideline of unsavedGuidelines) {
      // to ensure new guidelines are created in order
      await guideline.save();
    }
  }

  @dropTask
  *save(): TaskGenerator<void> {
    try {
      yield this._saveChangedGuidelines();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
    }
  }

  @action
  sendPreviewSettingsUpdatedMessage() {
    this.aiAgentGuidelineService.sendPreviewSettingsUpdatedMessage();
  }

  get userIsTyping(): boolean {
    return !!this.aiAgentGuidelineService.typingTimeout;
  }

  willDestroy() {
    // We need to clear the timeout in case the user navigates away from the page while typing
    // otherwise the message will be sent to the iframe even after the page has been unloaded
    super.willDestroy();
    if (this.aiAgentGuidelineService.typingTimeout) {
      clearTimeout(this.aiAgentGuidelineService.typingTimeout);
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  @action
  togglePreview() {
    this.showPreview = !this.showPreview;
  }

  @action
  doSearch(item: any) {
    this.searchTerm = item.target.value.toLowerCase();
  }

  @action
  resetSearch() {
    this.searchTerm = '';
  }

  @action
  onCategoryFilterChange(categories: EscalationCategoryType[]) {
    this.selectedCategories = categories;
  }

  @action
  onChannelFilterChange(selectedChannels: string[]) {
    this.selectedChannels = selectedChannels;
  }

  @action
  onAudienceFilterChange(audienceIds: number[]) {
    this.selectedAiContentSegmentIds = audienceIds;
  }

  @action
  changeTab(tab: string) {
    this.activeTab = tab;
    this.router.transitionTo({ queryParams: { escalation_tab: tab } });

    // Clear filters when switching tabs
    this.selectedCategories = [];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Escalation': typeof AiAgentEscalation;
  }
}
